<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-0vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <!-- Hero -->
    <section id="hero">
      <div class="overflow-hidden mt-10">
        <div
          class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col items-center gap-y-8 relative z-10">
          <span
            class="inline-flex items-center gap-x-2 bg-[#ff5f89]/3 text-xs font-semibold px-4 py-2 rounded-full border border-[#ff5f89]/20">
            <span class="w-2 h-2 bg-[#FF3696] rounded-full animate-pulse shrink-0"></span>
            <span class="text-gradient-primary">{{
              t('pages.campaigns.gloriafood.hero.badge')
            }}</span>
          </span>

          <h1
            class="text-[32px] leading-[42px] lg:text-[54px] lg:leading-[62px] font-bold font-markPro text-dark text-center tracking-[-0.1rem] max-w-[820px]">
            {{ t('pages.campaigns.gloriafood.hero.title_1') }}<br>
            <span class="text-gradient-primary">{{
              t('pages.campaigns.gloriafood.hero.title_em')
            }}</span>
          </h1>

          <p class="text-lighter text-lg lg:text-xl font-semibold text-center max-w-[640px]">
            {{ t('pages.campaigns.gloriafood.hero.subtitle') }}
          </p>

          <div
            class="flex items-center gap-x-3 bg-gradient-to-r from-[#FF3696]/8 to-[#FF7C53]/8 border border-[#FF3696]/20 rounded-xl px-6 py-4 text-dark font-semibold text-center">
            <i class="bi bi-gift-fill text-gradient-primary text-lg shrink-0"></i>
            <p>
              {{ t('pages.campaigns.gloriafood.hero.offer_pt1') }}
              <span class="text-gradient-primary">{{
                t('pages.campaigns.gloriafood.hero.offer_date')
              }}</span
              >{{ t('pages.campaigns.gloriafood.hero.offer_pt2') }}
            </p>
          </div>

          <div class="flex flex-col sm:flex-row gap-4">
            <a
              v-track-event="'onboarding_start'"
              :href="REGISTER_URL"
              class="btn btn-lg btn-primary whitespace-nowrap">
              {{ t('pages.campaigns.gloriafood.hero.cta_primary') }}
            </a>
            <a href="#contact" class="btn btn-lg btn-dark whitespace-nowrap">
              {{ t('pages.campaigns.gloriafood.hero.cta_secondary') }}
            </a>
          </div>

          <p class="flex items-center gap-x-2 text-sm text-lighter font-semibold">
            <i class="bi bi-check-circle-fill text-gradient-primary"></i>
            <span>{{ t('pages.campaigns.gloriafood.hero.reassurance') }}</span>
          </p>
        </div>
      </div>
    </section>

    <!-- Countdown -->
    <section id="countdown">
      <div class="fullscreen bg-dark py-14 lg:py-20">
        <div class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col items-center gap-y-8">
          <p class="text-xs font-semibold text-white/50 uppercase tracking-widest text-center">
            {{ t('pages.campaigns.gloriafood.countdown.label') }}
          </p>

          <div class="flex items-start justify-center gap-x-6 lg:gap-x-10">
            <template v-for="(unit, i) in countdownUnits" :key="unit.key">
              <span
                v-if="i > 0"
                class="text-3xl lg:text-5xl font-bold text-white/20 leading-none pt-1"
                aria-hidden="true"
                >:</span
              >
              <div class="text-center min-w-[64px] lg:min-w-[80px]">
                <span
                  class="block text-[40px] lg:text-[56px] leading-none font-bold font-markPro text-gradient-primary"
                  >{{ unit.value }}</span
                >
                <span
                  class="block text-[11px] lg:text-xs font-semibold text-white/40 uppercase tracking-widest mt-2"
                  >{{ unit.label }}</span
                >
              </div>
            </template>
          </div>

          <p class="text-sm text-white/50 font-semibold text-center">
            {{ t('pages.campaigns.gloriafood.countdown.cta_text') }}
            <a
              v-track-event="'onboarding_start'"
              :href="REGISTER_URL"
              class="text-white/80 underline whitespace-nowrap"
              >{{ t('pages.campaigns.gloriafood.countdown.cta_link') }} →</a
            >
          </p>
        </div>
      </div>
    </section>

    <!-- Trust bar -->
    <section id="trust" class="max-w-[1080px] lg:m-[auto]">
      <div
        class="w-full bg-white rounded-2xl border border-gray-100 drop-shadow grid md:grid-cols-2 lg:grid-cols-4">
        <div
          v-for="item in trustItems"
          :key="item.text"
          class="flex items-center gap-x-3 px-6 py-5 border-b border-gray-100 last:border-0 lg:border-b-0 lg:border-r lg:last:border-r-0">
          <span
            class="w-9 h-9 rounded-lg bg-gradient-primary flex items-center justify-center shrink-0">
            <i :class="['bi', item.icon, 'text-white']"></i>
          </span>
          <p class="text-sm font-semibold text-dark">{{ item.text }}</p>
        </div>
      </div>
    </section>

    <!-- What's happening -->
    <section id="timeline" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{
          t('pages.campaigns.gloriafood.timeline.kicker')
        }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.campaigns.gloriafood.timeline.heading') }}
        </h2>
        <p class="text-lighter font-semibold max-w-[600px]">
          {{ t('pages.campaigns.gloriafood.timeline.lead') }}
        </p>
      </div>

      <div class="relative w-full max-w-[680px]">
        <div
          class="absolute left-[22px] top-3 bottom-3 w-0.5 bg-gradient-to-b from-[#FF3696] to-[#FF7C53]"></div>

        <div
          v-for="item in timelineItems"
          :key="item.title"
          class="relative flex gap-x-6 pb-10 last:pb-0">
          <span
            class="w-11 h-11 rounded-full bg-white border border-light drop-shadow flex items-center justify-center shrink-0 relative z-10">
            <i :class="['bi', item.icon, 'text-gradient-primary']"></i>
          </span>
          <div class="flex flex-col gap-y-2 pt-1.5">
            <span class="text-xs font-bold text-lighter uppercase tracking-widest">{{
              item.date
            }}</span>
            <h3 class="font-markPro font-bold text-dark text-lg">{{ item.title }}</h3>
            <p class="text-lighter text-sm font-semibold leading-relaxed">{{ item.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Bistroo is the logical alternative -->
    <section id="why">
      <div class="fullscreen bg-dark py-16 lg:py-24">
        <div class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col gap-y-10">
          <div class="flex flex-col gap-y-4 text-center items-center">
            <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{
              t('pages.campaigns.gloriafood.why.kicker')
            }}</span>
            <h2
              class="text-2xl lg:text-4xl font-bold font-markPro text-white tracking-[-0.05rem] max-w-[640px]">
              {{ t('pages.campaigns.gloriafood.why.heading') }}
            </h2>
            <p class="text-white/50 font-semibold max-w-[580px]">
              {{ t('pages.campaigns.gloriafood.why.lead') }}
            </p>
          </div>

          <div class="grid md:grid-cols-2 gap-6">
            <div
              v-for="(item, i) in whyItems"
              :key="item.title"
              class="bg-white/3 border border-white/6 rounded-2xl p-8 flex gap-x-6 items-start">
              <span
                class="text-3xl lg:text-4xl font-bold font-markPro text-gradient-primary leading-none shrink-0"
                >{{ String(i + 1).padStart(2, '0') }}</span
              >
              <div class="flex flex-col gap-y-3">
                <h3 class="font-markPro font-bold text-white text-lg leading-snug">
                  {{ item.title }}
                </h3>
                <p class="text-white/55 text-sm font-semibold leading-relaxed">{{ item.text }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Everything you had with Gloriafood, and more -->
    <section id="features" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{
          t('pages.campaigns.gloriafood.features.kicker')
        }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.campaigns.gloriafood.features.heading') }}
        </h2>
        <p class="text-lighter font-semibold max-w-[620px]">
          {{ t('pages.campaigns.gloriafood.features.lead') }}
        </p>
      </div>

      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
        <div
          v-for="feature in featureItems"
          :key="feature.title"
          class="bg-white rounded-2xl border border-gray-100 drop-shadow p-8 flex flex-col gap-y-4">
          <span
            class="w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shrink-0">
            <i :class="['bi', feature.icon, 'text-white text-xl']"></i>
          </span>
          <h3 class="font-markPro font-bold text-dark text-lg">{{ feature.title }}</h3>
          <p class="text-lighter text-sm font-semibold leading-relaxed">{{ feature.text }}</p>
        </div>
      </div>
    </section>

    <!-- Offer -->
    <section id="offer">
      <div class="fullscreen bg-gradient-primary py-16 lg:py-20">
        <div
          class="max-w-[1080px] mx-auto px-4 lg:px-8 flex flex-col items-center gap-y-6 text-center">
          <h2
            class="text-2xl lg:text-4xl font-bold font-markPro text-white tracking-[-0.05rem] max-w-[700px]">
            {{ t('pages.campaigns.gloriafood.offer.heading') }}
          </h2>
          <p class="text-white/85 font-semibold text-lg max-w-[620px]">
            {{ t('pages.campaigns.gloriafood.offer.text') }}
          </p>
          <a
            v-track-event="'onboarding_start'"
            :href="REGISTER_URL"
            class="btn btn-lg bg-white text-[#FF3696] font-bold flex items-center gap-x-3">
            <span>{{ t('pages.campaigns.gloriafood.offer.button') }}</span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
          <p class="text-white/60 text-xs font-semibold max-w-[600px]">
            {{ t('pages.campaigns.gloriafood.offer.note') }}
          </p>
        </div>
      </div>
    </section>

    <!-- How it works -->
    <section id="steps" class="max-w-[1080px] lg:m-[auto]">
      <div class="flex flex-col gap-y-4 text-center items-center">
        <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{
          t('pages.campaigns.gloriafood.steps.kicker')
        }}</span>
        <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
          {{ t('pages.campaigns.gloriafood.steps.heading') }}
        </h2>
      </div>

      <div class="grid md:grid-cols-3 gap-6 w-full">
        <div
          v-for="(step, i) in stepItems"
          :key="step.title"
          class="bg-white rounded-2xl border border-gray-100 drop-shadow p-8 flex flex-col gap-y-3">
          <span class="text-4xl font-bold font-markPro text-gradient-primary leading-none">{{
            i + 1
          }}</span>
          <h3 class="font-markPro font-bold text-dark text-lg">{{ step.title }}</h3>
          <p class="text-lighter text-sm font-semibold leading-relaxed">{{ step.text }}</p>
        </div>
      </div>

      <a
        v-track-event="'onboarding_start'"
        :href="REGISTER_URL"
        class="btn btn-lg btn-primary flex items-center gap-x-3 -mt-6">
        <span>{{ t('pages.campaigns.gloriafood.steps.button') }}</span>
        <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
      </a>
    </section>

    <!-- Contact -->
    <section id="contact">
      <div class="fullscreen bg-[#F7F9FB] py-16 lg:py-0">
        <div class="max-w-[1080px] mx-auto px-4 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16">
          <div class="flex flex-col gap-y-6">
            <span class="text-sm font-bold text-gradient-primary uppercase tracking-widest">{{
              t('pages.campaigns.gloriafood.contact.kicker')
            }}</span>
            <h2 class="text-2xl lg:text-4xl font-bold font-markPro text-dark tracking-[-0.05rem]">
              {{ t('pages.campaigns.gloriafood.contact.heading') }}
            </h2>
            <p class="text-lighter font-semibold leading-relaxed">
              {{ t('pages.campaigns.gloriafood.contact.lead') }}
            </p>
            <ul class="flex flex-col gap-y-4">
              <li v-for="perk in contactPerks" :key="perk" class="flex items-start gap-x-3">
                <div
                  class="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center shrink-0 mt-0.5">
                  <i class="bi bi-check text-white text-xs"></i>
                </div>
                <span class="text-lighter font-semibold">{{ perk }}</span>
              </li>
            </ul>
          </div>

          <div class="bg-white rounded-2xl drop-shadow p-8 lg:p-10 flex flex-col gap-y-5">
            <h3 class="font-markPro font-bold text-dark text-xl">
              {{ t('pages.campaigns.gloriafood.contact.card.title') }}
            </h3>
            <p class="text-lighter font-semibold leading-relaxed">
              {{ t('pages.campaigns.gloriafood.contact.card.text') }}
            </p>
            <div class="flex flex-col gap-y-3">
              <a
                :href="CONTACT_FORM_URL"
                target="_blank"
                class="btn btn-lg btn-primary !w-full text-center flex items-center justify-center gap-x-3">
                <span>{{ t('pages.campaigns.gloriafood.contact.card.button') }}</span>
                <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
              </a>
              <a
                v-track-event="'onboarding_start'"
                :href="REGISTER_URL"
                class="btn btn-lg btn-primary-outline !w-full text-center">
                {{ t('pages.campaigns.gloriafood.contact.card.secondary') }}
              </a>
            </div>
            <p class="text-xs text-lighter font-semibold text-center">
              {{ t('pages.campaigns.gloriafood.contact.card.disclaimer') }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section id="faq">
      <div class="w-full rounded-2xl bg-[#F7F9FC] border border-[#E8EDF4] overflow-hidden">
        <div class="px-8 py-12 lg:px-20 lg:py-16 flex flex-col gap-y-10">
          <div
            class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-y-4 gap-x-12 max-w-[800px] mx-auto w-full">
            <div class="flex flex-col gap-y-3">
              <span
                class="inline-flex items-center gap-x-2 self-start text-sm font-semibold px-4 py-1.5 rounded-full border border-primary/25 bg-[#FFF5FB] text-lighter">
                <span
                  class="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF3696] to-[#FF7C53] inline-block flex-shrink-0"></span>
                {{ t('main.product_faq.label') }}
              </span>
              <h2
                class="text-[28px] leading-[34px] lg:text-[40px] lg:leading-[46px] font-bold font-markPro text-dark tracking-[-0.07rem]">
                {{ t('pages.campaigns.gloriafood.faq.title_pt1') }}
                <span class="text-gradient-primary">{{
                  t('pages.campaigns.gloriafood.faq.title_pt2')
                }}</span>
              </h2>
            </div>
            <p
              class="text-lighter font-semibold leading-relaxed lg:max-w-[38ch] lg:text-right lg:pb-1">
              {{ t('main.product_faq.not_listed') }}
            </p>
          </div>

          <FaqAccordion :items="faqs" />
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />
            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h2
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('pages.campaigns.gloriafood.cta.heading_1') }}<br>
            <span class="text-gradient-primary">{{
              t('pages.campaigns.gloriafood.cta.heading_em')
            }}</span>
          </h2>
          <p class="text-lg lg:text-[24px] text-lighter font-semibold max-w-[600px]">
            {{ t('pages.campaigns.gloriafood.cta.text') }}
          </p>
          <div class="flex flex-col sm:flex-row gap-4 mt-4">
            <a
              v-track-event="'onboarding_start'"
              :href="REGISTER_URL"
              class="btn btn-lg btn-primary flex items-center gap-x-3">
              <span>{{ t('pages.campaigns.gloriafood.cta.button_primary') }}</span>
              <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
            </a>
            <a :href="CONTACT_FORM_URL" target="_blank" class="btn btn-lg btn-dark">
              {{ t('pages.campaigns.gloriafood.cta.button_secondary') }}
            </a>
          </div>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import FaqAccordion from '@/components/FaqAccordion.vue'
import { useCountdown } from '@/composables/ui/useCountdown.ts'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

/** The day Gloriafood shuts down all of its services (CEST). */
const SHUTDOWN_AT = new Date('2027-04-30T23:59:59+02:00')

const REGISTER_URL = 'https://merchant.bistroo.nl/register'
const CONTACT_FORM_URL =
  'https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1'

const { t, tm } = useI18n()

const remaining = useCountdown(SHUTDOWN_AT)

const countdownUnits = computed(() =>
  (['months', 'days', 'hours', 'minutes'] as const).map((key) => ({
    key,
    value: remaining.value ? String(remaining.value[key]).padStart(2, '0') : '--',
    label: t(`pages.campaigns.gloriafood.countdown.units.${key}`),
  }))
)

const trustItems = computed(
  () => tm('pages.campaigns.gloriafood.trust.items') as { icon: string; text: string }[]
)
const timelineItems = computed(
  () =>
    tm('pages.campaigns.gloriafood.timeline.items') as {
      icon: string
      date: string
      title: string
      text: string
    }[]
)
const whyItems = computed(
  () => tm('pages.campaigns.gloriafood.why.items') as { title: string; text: string }[]
)
const featureItems = computed(
  () =>
    tm('pages.campaigns.gloriafood.features.items') as {
      icon: string
      title: string
      text: string
    }[]
)
const stepItems = computed(
  () => tm('pages.campaigns.gloriafood.steps.items') as { title: string; text: string }[]
)
const contactPerks = computed(() => tm('pages.campaigns.gloriafood.contact.perks') as string[])
const faqs = computed(
  () => tm('pages.campaigns.gloriafood.faq.items') as { q: string; a: string }[]
)

useHead(
  computed(() => ({
    title: t('pages.campaigns.gloriafood.seo.title'),
    meta: [{ name: 'description', content: t('pages.campaigns.gloriafood.seo.description') }],
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqs.value.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }))
)
</script>

<style scoped>
@reference "@/assets/css/main.css";
</style>
